// Client for the local measurement camera on the same computer as the
// browser. Since OraclaseWebGui serves the camera itself, the camera lives on
// the laser agent's URL (default http://127.0.0.1:8080) under /api/camera/*;
// the retired standalone camera agent (http://127.0.0.1:8081, status at
// /api/status) is still tried second so older workstations keep working.
//
// Deliberately uses plain fetch() instead of the app-wide axios instance:
// axios attaches the cloud Authorization token to every request, which must
// never be sent to the local agent (and would break its CORS preflight).

import { getAgentBaseUrl } from "./laserAgent";

const LEGACY_AGENT_URL = "http://127.0.0.1:8081";

// The endpoint that answered the last probe; frame/capture calls go there.
let activeEndpoint = null;

// Candidate endpoints in probe order: {baseUrl, statusPath}.
function cameraEndpoints() {
    return [
        { baseUrl: getAgentBaseUrl(), statusPath: "/api/camera/status" },
        { baseUrl: localStorage.getItem("cameraAgentUrl") || LEGACY_AGENT_URL, statusPath: "/api/status" },
    ];
}

function currentBaseUrl() {
    return (activeEndpoint || cameraEndpoints()[0]).baseUrl;
}

function agentHeaders(extra = {}) {
    const headers = { ...extra };
    const token = localStorage.getItem("cameraAgentToken");
    if (token) {
        headers["X-Camera-Agent-Token"] = token;
    }
    return headers;
}

async function agentFetchRaw(url, options = {}, timeoutMs = 0) {
    const controller = new AbortController();
    const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        return await fetch(url, {
            ...options,
            headers: agentHeaders(options.headers || {}),
            signal: controller.signal,
        });
    } finally {
        if (timer) clearTimeout(timer);
    }
}

async function agentFetchJson(url, options = {}, timeoutMs = 0) {
    const response = await agentFetchRaw(url, options, timeoutMs);
    let payload = null;
    try {
        payload = await response.json();
    } catch (parseError) {
        payload = null;
    }
    if (!response.ok) {
        const message = payload && payload.error ? payload.error : `Camera agent request failed (${response.status}).`;
        const error = new Error(message);
        error.status = response.status;
        throw error;
    }
    return payload;
}

// Probe the camera. Never throws: returns {available: false} when no camera
// endpoint answers (no agent running, camera off on this workstation),
// otherwise the status payload plus {available: true}. The first endpoint
// that answers becomes the target for frames and captures.
export async function probeCameraAgent({ timeoutMs = 2000 } = {}) {
    let lastError = "Camera agent not reachable.";
    for (const endpoint of cameraEndpoints()) {
        try {
            const status = await agentFetchJson(
                `${endpoint.baseUrl}${endpoint.statusPath}`,
                { method: "GET" },
                timeoutMs
            );
            activeEndpoint = endpoint;
            return { available: true, ...status };
        } catch (error) {
            lastError = error.message;
        }
    }
    activeEndpoint = null;
    return { available: false, error: lastError };
}

// One live-view frame as a Blob (image/jpeg), or null while live view is
// unavailable (camera off, folder_watch backend, agent busy). The preview
// polls this a few times per second and swaps object URLs.
export async function getLiveViewFrame() {
    try {
        const response = await agentFetchRaw(`${currentBaseUrl()}/api/camera/frame`, { method: "GET" }, 3000);
        if (!response.ok) return null;
        return await response.blob();
    } catch (error) {
        return null;
    }
}

// Trigger a capture on the camera. Slow by design (shutter + RAW download,
// and in folder_watch mode it waits for the operator's click in Imaging Edge
// Remote) - the timeout must stay generous.
function triggerCapture() {
    return agentFetchJson(
        `${currentBaseUrl()}/api/camera/capture`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: "{}",
        },
        130000
    );
}

// Fetch a finished capture as a File, named like the camera named it
// (e.g. DSC00042.ARW) so downstream extension checks behave exactly as if
// the user had picked the file from disk.
async function fetchCaptureFile(captureId, filename) {
    const response = await agentFetchRaw(
        `${currentBaseUrl()}/api/camera/capture/file?id=${encodeURIComponent(captureId)}`,
        { method: "GET" },
        120000
    );
    if (!response.ok) {
        throw new Error(`Could not fetch the captured file (${response.status}).`);
    }
    const blob = await response.blob();
    return new File([blob], filename, { type: "application/octet-stream" });
}

// Convenience: capture and return the image as a File in one call.
export async function captureToFile() {
    const result = await triggerCapture();
    return fetchCaptureFile(result.captureId, result.filename);
}
